/**
 * Admin-only manual-edits panel for Clothing & Accessories + Hair.
 *
 * Pencil icons per row, "+ Add" button, soft-delete with reset, and
 * "edited" badges whenever the current value differs from the
 * captured source value. All mutations go through the
 * /api/admin/cases/{id}/manual-edits/* endpoints, which mirror the
 * overlay to canonical + every linked external sibling.
 *
 * This component is rendered ONLY when the case detail page detects
 * an admin user. Non-admins continue to see the standard read-only
 * Clothing & Accessories block.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Pencil, Plus, Trash2, RotateCcw, Save, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const auth = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const isEdited = (item) =>
  item.source !== 'manual' && (
    (item.original_label != null && item.label !== item.original_label)
    || (item.original_description != null
        && item.description !== item.original_description)
  );

export default function ManualEditPanel({ caseId, sourceCase, onChange }) {
  const [overlay, setOverlay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // {mode, item?, field?}
  const [hairOpen, setHairOpen] = useState(null); // 'head_hair' | 'facial_hair'
  const [hairDraft, setHairDraft] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Fetch overlay (seeds from source on first read) ──────────────
  const fetchOverlay = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/cases/${caseId}/manual-edits`,
        { headers: auth() });
      if (!r.ok) {
        if (r.status === 401 || r.status === 403) {
          // Silently no-op for non-admin users
          setOverlay(null);
          return;
        }
        throw new Error(`HTTP ${r.status}`);
      }
      const data = await r.json();
      setOverlay(data);
    } catch (e) {
      console.error('manual-edits fetch failed', e);
      toast.error('Could not load edit overlay');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { if (caseId) fetchOverlay(); }, [caseId, fetchOverlay]);

  // ── Resolve hair display values (overlay → source fallback) ──────
  const resolveHair = (field) => {
    const o = overlay?.manual_edits?.hair?.[field];
    if (o?.value != null) {
      return { value: o.value, edited: true,
               editedAt: o.edited_at, editedBy: o.edited_by };
    }
    const src = overlay?.source_hair?.[field];
    return { value: src || '', edited: false };
  };

  // ── Save handlers ────────────────────────────────────────────────
  const saveClothingItem = async (item, isNew = false) => {
    setSaving(true);
    try {
      const url = isNew
        ? `${API_URL}/api/admin/cases/${caseId}/manual-edits/clothing`
        : `${API_URL}/api/admin/cases/${caseId}/manual-edits/clothing/${item.id}`;
      const r = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: auth(),
        body: JSON.stringify(item),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(isNew ? 'Item added' : 'Item saved');
      setEditing(null);
      await fetchOverlay();
      if (onChange) onChange();
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    } finally { setSaving(false); }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(
      item.source === 'manual'
        ? 'Delete this user-added item? (Cannot be undone.)'
        : 'Hide this source item? (Original NamUs / Doe Network text '
          + 'is preserved and can be restored via "Reset to source".)')) return;
    setSaving(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/cases/${caseId}/manual-edits/clothing/${item.id}`,
        { method: 'DELETE', headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Deleted');
      await fetchOverlay();
      if (onChange) onChange();
    } catch (e) {
      toast.error('Delete failed: ' + e.message);
    } finally { setSaving(false); }
  };

  const resetItem = async (item) => {
    setSaving(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/cases/${caseId}/manual-edits/clothing/${item.id}/reset`,
        { method: 'POST', headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Reverted to source');
      await fetchOverlay();
      if (onChange) onChange();
    } catch (e) {
      toast.error('Reset failed: ' + e.message);
    } finally { setSaving(false); }
  };

  const saveHair = async (field) => {
    setSaving(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/cases/${caseId}/manual-edits/hair`,
        { method: 'PUT', headers: auth(),
          body: JSON.stringify({ [field]: hairDraft }) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Hair description saved');
      setHairOpen(null);
      await fetchOverlay();
      if (onChange) onChange();
    } catch (e) {
      toast.error('Save failed: ' + e.message);
    } finally { setSaving(false); }
  };

  const resetHair = async (field) => {
    setSaving(true);
    try {
      const r = await fetch(
        `${API_URL}/api/admin/cases/${caseId}/manual-edits/hair/reset?field=${field}`,
        { method: 'POST', headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Reverted to source');
      await fetchOverlay();
      if (onChange) onChange();
    } catch (e) {
      toast.error('Reset failed: ' + e.message);
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="text-xs text-muted-foreground italic"
           data-testid="manual-edit-panel-loading">
        Loading edit overlay…
      </div>
    );
  }
  if (!overlay) return null;

  const items = (overlay.manual_edits?.clothing?.items || [])
    .filter((i) => !i.deleted);
  const deletedItems = (overlay.manual_edits?.clothing?.items || [])
    .filter((i) => i.deleted);

  return (
    <div className="space-y-6" data-testid="manual-edit-panel">
      {/* ── Hair ─────────────────────────────────────────────────── */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border
                      border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <h4 className="font-semibold mb-3 text-amber-700
                       dark:text-amber-400 flex items-center gap-2">
          Hair Description
          <span className="text-xs font-normal text-amber-600/70">
            (admin edits)
          </span>
        </h4>
        <div className="space-y-3">
          {['head_hair', 'facial_hair'].map((field) => {
            const r = resolveHair(field);
            return (
              <div key={field}
                   className="flex items-start gap-2"
                   data-testid={`hair-row-${field}`}>
                <Badge variant="outline"
                       className="shrink-0 text-xs border-amber-300
                                  text-amber-700 capitalize">
                  {field.replace('_', ' ')}
                </Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-muted-foreground
                                  break-words">
                    {r.value || (
                      <span className="italic opacity-60">
                        not recorded
                      </span>
                    )}
                  </div>
                  {r.edited && (
                    <div className="text-[10px] mt-0.5
                                    text-amber-700/80 font-medium">
                      EDITED · {r.editedAt?.slice(0, 10) || ''}
                    </div>
                  )}
                </div>
                <Button
                  size="sm" variant="ghost" className="h-7 w-7 p-0"
                  data-testid={`hair-edit-${field}`}
                  onClick={() => {
                    setHairDraft(r.value || '');
                    setHairOpen(field);
                  }}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                {r.edited && (
                  <Button
                    size="sm" variant="ghost"
                    className="h-7 w-7 p-0 text-amber-700"
                    title="Revert to source"
                    data-testid={`hair-reset-${field}`}
                    onClick={() => resetHair(field)}>
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Clothing & Accessories ──────────────────────────────── */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border
                      border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-blue-700
                         dark:text-blue-400 flex items-center gap-2">
            Clothing & Accessories
            <span className="text-xs font-normal text-blue-600/70">
              (admin edits)
            </span>
          </h4>
          <Button
            size="sm" variant="outline" className="h-7 text-xs"
            data-testid="clothing-add-btn"
            onClick={() => setEditing({
              mode: 'add',
              item: { id: 'new', label: '', description: '',
                      deleted: false, source: 'manual' },
            })}>
            <Plus className="h-3 w-3 mr-1" /> Add
          </Button>
        </div>
        <div className="space-y-2">
          {items.length === 0 && (
            <div className="text-xs italic text-muted-foreground">
              No clothing or accessories on file. Use "Add" to record
              something the agency or a witness has reported.
            </div>
          )}
          {items.map((item) => {
            const edited = isEdited(item);
            return (
              <div key={item.id}
                   className="flex items-start gap-2 group"
                   data-testid={`clothing-row-${item.id}`}>
                <Badge variant="outline"
                       className="shrink-0 text-xs border-blue-300
                                  text-blue-700 max-w-[120px] truncate">
                  {item.label}
                </Badge>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-muted-foreground
                                   break-words">
                    {item.description}
                  </span>
                  <div className="flex items-center gap-2 mt-0.5">
                    {item.source === 'manual' && (
                      <span className="text-[10px] text-emerald-700/80
                                       font-medium">
                        ADMIN-ADDED
                      </span>
                    )}
                    {edited && (
                      <span className="text-[10px] text-amber-700/80
                                       font-medium">
                        EDITED ·{' '}
                        {item.edited_at?.slice(0, 10) || ''}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm" variant="ghost" className="h-7 w-7 p-0"
                  data-testid={`clothing-edit-${item.id}`}
                  onClick={() => setEditing({
                    mode: 'edit',
                    item: { ...item },
                  })}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                {edited && item.source !== 'manual' && (
                  <Button
                    size="sm" variant="ghost"
                    className="h-7 w-7 p-0 text-amber-700"
                    title="Revert to source"
                    data-testid={`clothing-reset-${item.id}`}
                    onClick={() => resetItem(item)}>
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  size="sm" variant="ghost"
                  className="h-7 w-7 p-0 text-rose-700"
                  data-testid={`clothing-delete-${item.id}`}
                  onClick={() => deleteItem(item)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            );
          })}
          {deletedItems.length > 0 && (
            <details className="mt-3 pt-2 border-t border-blue-200/70">
              <summary className="text-xs cursor-pointer
                                  text-muted-foreground hover:text-blue-700">
                {deletedItems.length} hidden source item
                {deletedItems.length === 1 ? '' : 's'} ·
                click to review / restore
              </summary>
              <div className="mt-2 space-y-1">
                {deletedItems.map((item) => (
                  <div key={item.id}
                       className="flex items-start gap-2 opacity-60">
                    <Badge variant="outline"
                           className="shrink-0 text-xs line-through">
                      {item.original_label || item.label}
                    </Badge>
                    <span className="text-sm line-through flex-1">
                      {item.original_description || item.description}
                    </span>
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 text-xs text-blue-700"
                      onClick={() => resetItem(item)}>
                      Restore
                    </Button>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>

      {/* ── Edit / Add modal ─────────────────────────────────────── */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing?.mode === 'add' ? 'Add clothing or accessory'
                                       : 'Edit item'}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium">Label</label>
                <Input
                  value={editing.item.label}
                  onChange={(e) => setEditing({
                    ...editing,
                    item: { ...editing.item, label: e.target.value },
                  })}
                  placeholder="e.g. Watch, Dress, Necklace…"
                  data-testid="clothing-edit-label"
                />
              </div>
              <div>
                <label className="text-xs font-medium">Description</label>
                <Textarea
                  value={editing.item.description}
                  onChange={(e) => setEditing({
                    ...editing,
                    item: { ...editing.item,
                            description: e.target.value },
                  })}
                  rows={4}
                  placeholder="Brand, color, distinguishing marks…"
                  data-testid="clothing-edit-description"
                />
              </div>
              {editing.item.original_description
                && editing.item.original_description
                   !== editing.item.description && (
                <div className="text-[11px] p-2 rounded bg-amber-50
                                border border-amber-200 text-amber-800">
                  <span className="font-semibold">Source value:</span>
                  {' '}
                  <span className="italic">
                    {editing.item.original_label} —{' '}
                    {editing.item.original_description}
                  </span>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditing(null)}>
              <X className="h-3.5 w-3.5 mr-1" /> Cancel
            </Button>
            <Button
              disabled={saving
                || !editing?.item?.label
                || !editing?.item?.description}
              data-testid="clothing-edit-save"
              onClick={() => saveClothingItem(
                editing.item, editing.mode === 'add')}>
              <Save className="h-3.5 w-3.5 mr-1" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!hairOpen} onOpenChange={(o) => !o && setHairOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Edit {hairOpen?.replace('_', ' ')}
            </DialogTitle>
          </DialogHeader>
          <Textarea
            value={hairDraft}
            onChange={(e) => setHairDraft(e.target.value)}
            rows={3}
            placeholder="e.g. 'short black, shoulder-length' or details from a witness"
            data-testid="hair-edit-textarea"
          />
          {overlay?.source_hair?.[hairOpen]
            && overlay.source_hair[hairOpen] !== hairDraft && (
            <div className="text-[11px] p-2 rounded bg-amber-50
                            border border-amber-200 text-amber-800">
              <span className="font-semibold">Source value:</span>{' '}
              <span className="italic">
                {overlay.source_hair[hairOpen]}
              </span>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setHairOpen(null)}>
              <X className="h-3.5 w-3.5 mr-1" /> Cancel
            </Button>
            <Button
              disabled={saving}
              data-testid="hair-edit-save"
              onClick={() => saveHair(hairOpen)}>
              <Save className="h-3.5 w-3.5 mr-1" />
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
